import { useState } from "react";
import { useCartContext } from "@/providers/CartContextProvider";
import { addCouponCodeToCheckout } from "@/lib/bigcommerce/api/discount";
import { StoreRegion } from "@/lib/_config/stores";

type CouponApplicationCallbacks = {
	onSuccess: () => void;
	onFailure: () => void;
	onNoCart: () => void;
};

export const useCouponApplication = (
	cart: any | null,
	promotionCode: string | null,
	store: StoreRegion,
	callbacks: CouponApplicationCallbacks,
) => {
	const [isProcessing, setIsProcessing] = useState(false);
	const { refreshCart } = useCartContext();
	const { onSuccess, onFailure, onNoCart } = callbacks;

	const applyCoupon = async (e: React.MouseEvent) => {
		e.preventDefault();

		if (!cart?.id) {
			console.warn("No cart available");
			callbacks.onNoCart();
			return;
		}

		if (isProcessing) {
			return; // Prevent multiple clicks while processing
		}

		setIsProcessing(true);

		try {
			// Get the coupon code from the promotion code context
			const couponCode = promotionCode || "SHIPPING"; // Fallback to hardcoded value if no promotion code

			const result = await addCouponCodeToCheckout(cart.id, couponCode, store);

			if (result?.status === 200) {
				console.info("Coupon applied successfully");
				// Refresh cart to show updated discount
				await refreshCart();
				onSuccess();
			} else {
				console.error("Failed to apply coupon");
				onFailure();
			}
		} catch (error) {
			console.error("Error applying coupon:", error);
			onFailure();
		} finally {
			setIsProcessing(false);
		}
	};

	return { applyCoupon, isProcessing };
};
